import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import DateAndTime from 'date-and-time';

import Button from 'components/Buttons/Button';
import {
  fnAddEventRegistrationEditToCart,
  fnAddEventToCart,
  fnAddWaitlistEventToCart,
} from 'components/EventRegistration/redux/eventHelpers';
import { fnAddGuestToEventInCart } from 'components/EventRegistration/redux/guestHelpers';
import {
  currentUserFirstLastNameSelector,
  currentUserLookupIdSelector,
  selectCurrentUserEmail,
} from 'redux/selectors';
import { selectEventRegNavSteps } from 'components/EventRegistration/EventRegistrationContainer/EventRegistrationNavBar/redux/selectors';
import {
  fnIsEventAtCapacity,
  fnIsEventRegistrable,
} from 'components/Events/helpers';
import useIsUserAuthenticated from 'utils/hooks/useIsUserAuthenticated';
import * as CheckoutActions from 'components/Checkout/redux/actions';
import ConfirmRsvpNoModal from '../ConfirmRsvpNoModal';

import RegistrationWindowClosed from '../RegistrationWindowClosed';
import CancelEventRegistrationModal from '../CancelEventRegistration';
import { fnBeginRegistrationCancellation } from '../CancelEventRegistration/helpers';

const IndividualEventDetailsFooter = ({
  oEvent,
  fnSetShowSignInOrContinueAsGuestModal,
}) => {
  const fnNavigate = useNavigate();
  const fnDispatch = useDispatch();
  const sCurrentUserLookupId = useSelector(currentUserLookupIdSelector);
  const oCurrentUserName = useSelector(currentUserFirstLastNameSelector);
  const sCurrentUserEmail = useSelector(selectCurrentUserEmail);
  const [bShowRsvpNoConfirmModal, fnSetShowRsvpNoConfirmModal] =
    useState(false);
  const [bHasRsvpdNo, fnSetHasRsvpdNo] = useState(false);
  const [
    bShowCancelRegistrationConfirmModal,
    fnSetShowCancelRegistrationConfirmModal,
  ] = useState(false);
  const bIsUserAuthenticated = useIsUserAuthenticated();
  const [sFirstEventRegStep] = useSelector((state) =>
    selectEventRegNavSteps(state, bIsUserAuthenticated)
  );
  const [bIsSubmitting, fnSetIsSubmitting] = useState(false);

  const bIsInvited = [true, 1].includes(oEvent?.INVITATION?.IS_INVITED);
  const { RSVP_STATUS, HAS_INVITEES } = oEvent?.INVITATION || '';
  const bIsWaitlistEvent = [true, 1].includes(
    oEvent?.REGISTRATION?.ALLOW_WAITLIST
  );
  const { IS_REGISTERED } = oEvent?.REGISTRATION || '';
  const bIsHost = oEvent?.MY_PARTY?.HOST?.LOOKUPID === sCurrentUserLookupId;
  const bIsWaitlisted = [true, 1].includes(oEvent?.REGISTRATION?.IS_WAITLISTED);
  const bShowRsvpButtons =
    bIsInvited || (HAS_INVITEES === 1 && !bIsUserAuthenticated); // Show RSVP buttons if the user is invited or if the event has invitees and the user is not authenticated
  const bIsSubEvent = !!oEvent?.MAIN_EVENT?.LOOKUPID;

  const fnSetCheckoutUserInfo = () => {
    fnDispatch(
      CheckoutActions.update({
        fullName: oCurrentUserName.sName,
        firstName: oCurrentUserName.sPreferredName,
        lastName: oCurrentUserName.sLastName,
        email: sCurrentUserEmail,
        userLookupId: sCurrentUserLookupId,
      })
    );
  };

  // Add this event to the cart and add the current user as a guest
  const fnBeginRegistration = () => {
    // The current event registration checkout flow only supports registering
    // for one single-level event or multiple sub events from the same main event at a time.
    // So, we need to reset the cart to start fresh.
    fnDispatch(CheckoutActions.reset({}));

    fnSetCheckoutUserInfo();
    fnAddEventToCart(oEvent);
    if (!bIsUserAuthenticated) {
      fnSetShowSignInOrContinueAsGuestModal(true);
    } else {
      fnSetIsSubmitting(true);
      fnAddGuestToEventInCart(
        `${oCurrentUserName.sPreferredName} ${oCurrentUserName.sLastName}`,
        sCurrentUserLookupId,
        oEvent.LOOKUPID,
        null,
        'currentHost'
      );
    }
  };

  const fnJoinWaitlist = () => {
    if (!bIsUserAuthenticated) {
      fnSetShowSignInOrContinueAsGuestModal(true);
      return;
    }

    // The current event registration checkout flow only supports registering
    // for one single-level event or multiple sub events from the same main event at a time.
    // So, we need to reset the cart to start fresh.
    fnDispatch(CheckoutActions.reset({}));

    fnSetCheckoutUserInfo();
    fnSetIsSubmitting(true);
    fnAddWaitlistEventToCart(oEvent);
    fnAddGuestToEventInCart(
      `${oCurrentUserName.sPreferredName} ${oCurrentUserName.sLastName}`,
      sCurrentUserLookupId,
      oEvent.LOOKUPID,
      null,
      'currentHost'
    );
  };

  const fnBeginEditingRegistration = () => {
    fnSetCheckoutUserInfo();
    fnAddEventRegistrationEditToCart(oEvent);
    fnNavigate('/events/register/review');
  };

  // Navigate to the first registration step
  // Doing this in a useEffect instead of in the submit handler allows the
  // `selectEventRegNavSteps` selector to re-calculate against the new event(s) in the cart.
  useEffect(() => {
    if (bIsSubmitting && sFirstEventRegStep) {
      fnNavigate(sFirstEventRegStep);
    }
  }, [sFirstEventRegStep, fnNavigate, bIsSubmitting]);

  const bIsEventRegistrable = fnIsEventRegistrable(oEvent);
  const bIsEventAtCapacity = fnIsEventAtCapacity(oEvent);
  const bIsRegistrationWindowClosed = oEvent.REGISTRATION.CLOSE_DATETIME
    ? DateAndTime.parse(
        oEvent.REGISTRATION.CLOSE_DATETIME,
        'YYYY-MM-DDTHH:mm:ss'
      ) < new Date()
    : false;

  let registrationButtons = '';
  switch (true) {
    case IS_REGISTERED === 1 && bIsHost:
      registrationButtons = (
        <>
          <Button
            fnHandleClick={fnBeginEditingRegistration}
            className='eventDetailsFooter__editRegistrationButton'
          >
            Edit Registration
          </Button>
          <Button
            sFlavor='tertiary'
            fnHandleClick={() =>
              fnBeginRegistrationCancellation(
                oEvent,
                fnSetShowCancelRegistrationConfirmModal,
                fnDispatch
              )
            }
            className='eventDetailsFooter__cancelRegistrationButton'
          >
            Cancel Registration
          </Button>
        </>
      );
      break;
    case bIsSubEvent:
      registrationButtons = null; // Registration happens at the main event level instead
      break;
    case bIsRegistrationWindowClosed:
      registrationButtons = (
        <RegistrationWindowClosed
          sCloseDateTime={oEvent.REGISTRATION.CLOSE_DATETIME}
          sTimeZone={oEvent.TIME_ZONE}
        />
      );
      break;
    case bIsEventAtCapacity && bIsWaitlistEvent && !bIsWaitlisted:
      registrationButtons = (
        <Button
          fnHandleClick={fnJoinWaitlist}
          sCypressId='waitlist-button'
          className='eventDetailsFooter__registerButton'
        >
          Join the waitlist
        </Button>
      );
      break;
    case !bIsEventRegistrable || (bIsWaitlisted && !bIsEventAtCapacity):
      // Don't show registration buttons when the event is cancelled or the user is already registered
      registrationButtons = null;
      break;
    case RSVP_STATUS === 'Declined':
    case bHasRsvpdNo: // intentional fall-through
      registrationButtons = (
        <p className='t-paragraph--small' data-cy='rsvp-decline-message'>
          You declined this invitation.
        </p>
      );
      break;

    case bShowRsvpButtons:
      registrationButtons = (
        <div className='individualEventDetails__rsvpWrapper'>
          <Button
            sFlavor='underline'
            fnHandleClick={() => fnSetShowRsvpNoConfirmModal(true)}
            sCypressId='rsvp-no-button'
          >
            RSVP No
          </Button>
          <Button
            fnHandleClick={fnBeginRegistration}
            sCypressId='rsvp-yes-button'
          >
            RSVP Yes
          </Button>
        </div>
      );
      break;
    default:
      registrationButtons = (
        <Button
          fnHandleClick={fnBeginRegistration}
          sCypressId='register-button'
          className='eventDetailsFooter__registerButton'
        >
          Register
        </Button>
      );
      break;
  }

  return (
    <div
      className={`eventDetails__actionButtons ${
        bIsSubEvent ? 'eventDetails__actionButtons--subEvent' : ''
      }`}
    >
      {bIsUserAuthenticated && (
        <Button
          sFlavor='secondary'
          fnHandleClick={() => fnNavigate(-1)}
          className='individualEventDetails__back'
          sCypressId='back-button'
        >
          Back
        </Button>
      )}
      {registrationButtons && (
        <div className='eventDetailsFooter__right'>{registrationButtons}</div>
      )}
      {bShowRsvpNoConfirmModal && (
        <ConfirmRsvpNoModal
          sEventTitle={oEvent.TITLE}
          sEventLookupId={oEvent.LOOKUPID}
          fnSetShowRsvpNoConfirmModal={fnSetShowRsvpNoConfirmModal}
          fnSetHasRsvpdNo={fnSetHasRsvpdNo}
          oEventContact={oEvent.CONTACT}
        />
      )}
      {bShowCancelRegistrationConfirmModal && (
        <CancelEventRegistrationModal
          bShowCancelRegistrationConfirmModal={
            bShowCancelRegistrationConfirmModal
          }
          fnSetShowCancelRegistrationConfirmModal={
            fnSetShowCancelRegistrationConfirmModal
          }
        />
      )}
    </div>
  );
};

IndividualEventDetailsFooter.propTypes = {
  oEvent: PropTypes.shape({
    TITLE: PropTypes.string,
    LOOKUPID: PropTypes.string,
    IS_CANCELLED: PropTypes.bool,
    TIME_ZONE: PropTypes.string,
    CONTACT: PropTypes.shape({
      NAME: PropTypes.string,
      EMAIL: PropTypes.string,
    }),
    INVITATION: PropTypes.shape({
      IS_INVITED: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      INVITEE_ID: PropTypes.string,
    }),
    MY_PARTY: PropTypes.shape({
      HOST: PropTypes.shape({
        LOOKUPID: PropTypes.string,
      }),
    }),
    REGISTRATION: PropTypes.shape({
      MAX_GUESTS: PropTypes.number,
      CLOSE_DATETIME: PropTypes.string,
      ALLOW_WAITLIST: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      IS_WAITLISTED: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    }),
    MAIN_EVENT: PropTypes.shape({
      LOOKUPID: PropTypes.string,
    }),
  }).isRequired,
  fnSetShowSignInOrContinueAsGuestModal: PropTypes.func.isRequired,
};

export default IndividualEventDetailsFooter;
